import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { CloseIcon } from "@/components/Icons";

/**
 * Tiny set of form / dialog primitives shared by the operator pages.
 * Kept dependency-free — no shadcn/ui imports — so the bundle stays
 * minimal and the Tailwind tokens flow through directly.
 */

export function PageContainer({
  title,
  subtitle,
  actions,
  children,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="px-4 md:px-8 py-6 max-w-screen-2xl mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          {subtitle && (
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              {subtitle}
            </p>
          )}
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">{title}</h1>
        </div>
        {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

export function Card({
  title,
  description,
  actions,
  children,
}: {
  title?: string;
  description?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-border bg-card p-4 md:p-5">
      {(title || actions) && (
        <header className="flex items-start justify-between gap-2 mb-3">
          <div>
            {title && <h2 className="text-sm font-semibold">{title}</h2>}
            {description && (
              <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
            )}
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-muted-foreground">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </span>
      <div className="mt-1">{children}</div>
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </label>
  );
}

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`w-full rounded-md border border-border bg-card px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-accent ${
        props.className ?? ""
      }`}
    />
  );
}

export function TextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={`w-full rounded-md border border-border bg-card px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-accent ${
        props.className ?? ""
      }`}
    />
  );
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      className={`w-full rounded-md border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-accent ${
        props.className ?? ""
      }`}
    />
  );
}

export function Button({
  variant = "default",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "default" | "primary" | "ghost" | "destructive";
}) {
  const v = (() => {
    switch (variant) {
      case "primary":
        return "bg-accent text-accent-foreground hover:bg-accent/90";
      case "ghost":
        return "border border-transparent text-foreground hover:bg-muted";
      case "destructive":
        return "border border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/20";
      default:
        return "border border-border bg-card text-foreground hover:bg-muted";
    }
  })();
  return (
    <button
      {...props}
      className={`inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed ${v} ${
        props.className ?? ""
      }`}
    />
  );
}

export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-background/70 backdrop-blur-sm p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        ref={dialogRef}
        className="w-full max-w-md rounded-lg border border-border bg-background shadow-2xl"
      >
        <header className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border">
          <h2 className="text-sm font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label={t("forms.close")}
          >
            <CloseIcon size={14} />
          </button>
        </header>
        <div className="p-4 max-h-[70vh] overflow-y-auto">{children}</div>
        {footer && (
          <footer className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

export function ErrorBanner({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
      {message}
    </div>
  );
}

export function Badge({
  children,
  tone = "neutral",
}: {
  children: React.ReactNode;
  tone?: "neutral" | "success" | "warning" | "danger" | "info";
}) {
  const className = (() => {
    switch (tone) {
      case "success":
        return "bg-emerald-500/15 text-emerald-400";
      case "warning":
        return "bg-amber-500/15 text-amber-400";
      case "danger":
        return "bg-destructive/15 text-destructive";
      case "info":
        return "bg-sky-500/15 text-sky-300";
      default:
        return "bg-muted text-muted-foreground";
    }
  })();
  return (
    <span
      className={`inline-flex items-center rounded-sm px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${className}`}
    >
      {children}
    </span>
  );
}

/**
 * Coerces an ApiError / Error / unknown into a human-readable string.
 * Phase 12 i18n: when no explicit {@code fallback} is passed, the
 * function reaches into the i18next instance directly (works in catch
 * blocks outside the React tree where `useTranslation()` isn't
 * available). Pass an explicit fallback to override.
 */
export function toErrorMessage(err: unknown, fallback?: string): string {
  if (err && typeof err === "object" && "status" in err && "body" in err) {
    const e = err as { status: number; body: unknown };
    if (e.body && typeof e.body === "object" && "error" in e.body) {
      return String((e.body as Record<string, unknown>).error);
    }
    return `${e.status}`;
  }
  if (err instanceof Error) return err.message;
  return fallback ?? i18n.t("forms.errorFallback");
}

// ─── Imperative confirm dialog ─────────────────────────────────────────────
// Replaces window.confirm() with a Tailwind-styled modal so destructive
// actions match the rest of the chrome (the Phase 12 DoD asks for a real
// confirmation modal, not the OS dialog).
//
// Usage: wrap the app once with <ConfirmProvider>, then in a component:
//
//   const confirm = useConfirm();
//   async function onEscalate() {
//     if (!(await confirm({
//       title: "Eskalera till människa? Engine pausas på denna lead.",
//       destructive: true,
//     }))) return;
//     // … proceed
//   }

export type ConfirmOptions = {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
};

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [state, setState] = useState<{
    opts: ConfirmOptions;
    resolve: (value: boolean) => void;
  } | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => setState({ opts, resolve }));
  }, []);

  function close(value: boolean) {
    if (state) state.resolve(value);
    setState(null);
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        open={state !== null}
        title={state?.opts.title ?? ""}
        onClose={() => close(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => close(false)}>
              {state?.opts.cancelLabel ?? t("forms.cancel")}
            </Button>
            <Button
              variant={state?.opts.destructive ? "destructive" : "primary"}
              onClick={() => close(true)}
              autoFocus
            >
              {state?.opts.confirmLabel ?? t("forms.confirm")}
            </Button>
          </>
        }
      >
        {state?.opts.body ? (
          <div className="text-sm text-muted-foreground">{state.opts.body}</div>
        ) : (
          // The title carries the full prompt; an empty body shows nothing
          // beyond the header bar. Keeping a placeholder so Modal always
          // renders a non-empty content slot.
          <div className="sr-only">{t("forms.confirmActionPrompt")}</div>
        )}
      </Modal>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const fn = useContext(ConfirmContext);
  if (!fn)
    throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return fn;
}
